import React, { useState } from "react";
import { PageHeader } from "@/CommonElements/PageTitle";
import { Col, Container, Row, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { PilgrimOverView } from "./PilgrimOverView";
import { PilgrimMakkahData } from "./PilgrimMakkahData";
import { PilgrimMadinaData } from "./PilgrimMadinahData";
import { DetailedData } from "./DetailedData";
import { NusukCardData } from "./NusukCardData";
import { ArrivalDepartureData } from "./ArrivalDepartureData";
import { Incidents } from "./Incidents";
import { useLocalizedText } from "@utils/hooks/useLocalizedText";
import { useParams } from "react-router-dom";
import { useGetPilgrimQuery } from "@services/piglgrims";
import { CampData } from "./CampData";
import { formatDate } from "@utils/helpers";
import BtnAsLink from "@Component/BtnAsLink";
import { HugeiconsIcon } from "@hugeicons/react";
import { PrinterIcon } from "@hugeicons/core-free-icons";
import PilgrimPrintSheet from "./PilgrimPrintSheet";
import { Card } from "@/CommonElements/Hajj/Card";
import { LabelValue } from "@Component/Value";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";

export const PilgrimDetails = () => {
  const { t, i18n } = useTranslation();
  const { getLocalizedText } = useLocalizedText();
  const { id } = useParams();
  const [printRequestKey, setPrintRequestKey] = useState(null);

  const { data: pilgrimRes, isLoading } = useGetPilgrimQuery(id);
  const pilgrim = pilgrimRes?.data?.data;

  // Helper function to format date
  const formatDateValue = (date) => {
    return formatDate(date, i18n.language, "DD MMMM YYYY HH:mm");
  };

  const pilgrimOverViewProps = {
    full_name_ar: pilgrim?.personal_profile?.name_ar,
    full_name_en: pilgrim?.personal_profile?.name_en,
    phone: pilgrim?.personal_profile?.phone_number ?? "-",
    pilgrim_number: pilgrim?.personal_profile?.hajj_id ?? "-",
    passport_number: pilgrim?.personal_profile?.passport_number ?? "-",
    nationality_id: pilgrim?.personal_profile?.nationality_id ?? "-",
    gender: getLocalizedText(
      pilgrim?.personal_profile?.gender?.ar ?? "-",
      pilgrim?.personal_profile?.gender?.en ?? "-"
    ),
    country_of_residence_id:
      pilgrim?.personal_profile?.residence_country_id ?? "-",
    state_city: pilgrim?.personal_profile?.state_city ?? "-",
    id_number: pilgrim?.personal_profile?.identifier_number ?? "-",
    age: pilgrim?.personal_profile?.age ?? "-",
    age_category: getLocalizedText(
      pilgrim?.personal_profile?.age_stage?.ar ?? "-",
      pilgrim?.personal_profile?.age_stage?.en ?? "-"
    ),
    pilgrim_type: pilgrim?.personal_profile?.haj_type,
    has_passport_receipt: pilgrim?.has_passport_receipt,
  };

  // -------------------- Detailed Data --------------------
  const detailedDataProps = {
    campaign_name: pilgrim?.detailed_information?.campaign_name,
    visa_number: pilgrim?.detailed_information?.visa_number,
    visa_source: pilgrim?.detailed_information?.visa_source,
    organizer_name: pilgrim?.detailed_information?.organizer_name,
    campaign_number: pilgrim?.detailed_information?.campaign_number,
    bag_number_madinah: pilgrim?.detailed_information?.bag_number_medina,
    group_number: pilgrim?.detailed_information?.group_number,
    group_name: pilgrim?.detailed_information?.group_name,
    pilgrim_status: pilgrim?.detailed_information?.pilgrim_status,
    reports: pilgrim?.detailed_information?.reports?.join?.(" - "),
    nusk_group_name: pilgrim?.detailed_information?.nusk_campaign_group_name,
    nusk_group_number: pilgrim?.detailed_information?.haj_campaign_group_number,
    incident_reports: pilgrim?.detailed_information?.incident_reports,
  };

  // -------------------- Makkah Data --------------------

  const makkahDataProps = {
    campaign_name: pilgrim?.makkah_data?.campaign_name,
    campaign_number: pilgrim?.makkah_data?.campaign_number,
    group_number: pilgrim?.makkah_data?.group_number,
    group_name: pilgrim?.makkah_data?.group_name,
    service_center_number_makkah: pilgrim?.makkah_data?.service_center_id,
    accommodation_address_makkah: pilgrim?.makkah_data?.address,
    locker_number_makkah: pilgrim?.makkah_data?.locker_number,
    arrival_date_makkah: formatDateValue(pilgrim?.makkah_data?.arrival_date),
    preliminary_departure_report_number_makkah:
      pilgrim?.makkah_data?.preliminary_departure_report,
  };

  // -------------------- Madina Data --------------------

  const madinahDataProps = {
    service_center_number_madinah:
      pilgrim?.medina_data?.service_center_id ?? "-",
    accommodation_address_madinah: pilgrim?.medina_data?.address ?? "-",
    locker_number_madinah: pilgrim?.medina_data?.locker_number ?? "-",
    arrival_date_madinah: formatDateValue(pilgrim?.medina_data?.arrival_date),
    preliminary_departure_report_number_madinah:
      pilgrim?.medina_data?.preliminary_departure_report ?? "-",
  };

  // -------------------- Nusuk Card Data --------------------

  const nusukCardDataProps = {
    nusuk_card_number: pilgrim?.nusuk_card?.card_number ?? "-",
    activation_date: formatDateValue(pilgrim?.nusuk_card?.activation_date),
    card_status: pilgrim?.nusuk_card?.card_status ?? "-",
    report_number: pilgrim?.nusuk_card?.report_number ?? "-",
  };

  // -------------------- Arrival Departure Data --------------------

  const arrivalDepartureDataProps = {
    entry_port: pilgrim?.arrival_departure?.arrival?.entry_port ?? "-",
    expected_arrival_date: formatDateValue(
      pilgrim?.arrival_departure?.arrival?.expected_arrival_date
    ),
    flight_number: pilgrim?.arrival_departure?.arrival?.flight_number ?? "-",
    actual_arrival_date: formatDateValue(
      pilgrim?.arrival_departure?.arrival?.actual_arrival_date
    ),
    direction: pilgrim?.arrival_departure?.arrival?.direction ?? "-",
    exit_port: pilgrim?.arrival_departure?.departure?.exit_port ?? "-",
    expected_departure_date: formatDateValue(
      pilgrim?.arrival_departure?.departure?.expected_departure_date
    ),
    departure_direction:
      pilgrim?.arrival_departure?.departure?.direction_upon_departure ?? "-",
    actual_departure_date: formatDateValue(
      pilgrim?.arrival_departure?.departure?.actual_departure_date
    ),
    nusuk_departure_report_number:
      pilgrim?.arrival_departure?.departure?.departure_report_number ?? "-",
    departure_confirmation_nusuk: formatDateValue(
      pilgrim?.arrival_departure?.departure?.proof_of_departure
    ),
  };

  const passportHandoverDate = pilgrim?.has_passport_receipt
    ? formatDateValue(pilgrim?.passport_handover?.receipt_at)
    : "";
  const passportHandover = pilgrim?.passport_handover;
  const formatBooleanValue = (value) =>
    value === true ? t("YES") : value === false ? t("NO") : "-";
  const formatEntityTypeValue = (value) => {
    if (!value) return "-";
    if (value === "internal" || value === 1 || value === "1") {
      return t("INTERNAL");
    }
    if (value === "external" || value === 2 || value === "2") {
      return t("EXTERNAL");
    }
    return value;
  };

  // -------------------- Incidents Data --------------------
  const incidentsDataProps = {
    incident_1: "missing",
    incident_date_1: formatDateValue("2024-01-18"),
    incident_2: "تأخير في الوصول",
    incident_date_2: formatDateValue("2024-01-19"),
  };
  return (
    <>
      <PageHeader title={t("PILGRIM_DETAILS")} />
      <Container fluid>
        {!isLoading && (
          <BtnAsLink
            className="btn-outline-primary btn-sm mb-3"
            icon={<HugeiconsIcon size={16} icon={PrinterIcon} />}
            onClick={() => setPrintRequestKey(`${id}-${Date.now()}`)}
          >
            {t("PRINT")}
          </BtnAsLink>
        )}
        <Row>
          <COL>
            <STACK_3>
              <PilgrimOverView
                {...pilgrimOverViewProps}
                isLoading={isLoading}
              />
              <PilgrimMakkahData {...makkahDataProps} isLoading={isLoading} />
              <PilgrimMadinaData {...madinahDataProps} isLoading={isLoading} />
            </STACK_3>
          </COL>
          <COL>
            <STACK_3>
              <DetailedData {...detailedDataProps} isLoading={isLoading} />
              <NusukCardData {...nusukCardDataProps} isLoading={isLoading} />
              <CampData {...(pilgrim?.camp_data ?? {})} isLoading={isLoading} />
              <Incidents {...incidentsDataProps} isLoading={isLoading} />
            </STACK_3>
          </COL>
          <COL>
            <STACK_3>
              <ArrivalDepartureData
                {...arrivalDepartureDataProps}
                isLoading={isLoading}
              />
              {
                pilgrim?.has_passport_receipt && (
                  <Card
                header={<span className="H5">{t("PASSPORT_RECEIPT_DOCUMENT")}</span>}
              >
                <STACK_4>
                  <LabelValue
                    loading={isLoading}
                    direction="vertical"
                    label={t("HAS_PASSPORT_RECEIPT")}
                    value={formatBooleanValue(pilgrim?.has_passport_receipt)}
                  />
                  <LabelValue
                    loading={isLoading}
                    direction="vertical"
                    label={t("PASSPORT_PHYSICALLY_DELIVERED")}
                    value={formatBooleanValue(
                      passportHandover?.passport_physically_delivered
                    )}
                  />
                  <LabelValue
                    loading={isLoading}
                    direction="vertical"
                    label={t("PASSPORT_RECEIPT_PRINTED")}
                    value={formatBooleanValue(
                      passportHandover?.passport_receipt_printed
                    )}
                  />
                  <LabelValue
                    loading={isLoading}
                    direction="vertical"
                    label={t("RECEIPTS_COUNT")}
                    value={passportHandover?.receipts_count ?? "-"}
                  />
                  <LabelValue
                    loading={isLoading}
                    direction="vertical"
                    label={t("ENTITY_NAME")}
                    value={passportHandover?.entity_name ?? "-"}
                  />
                  <LabelValue
                    loading={isLoading}
                    direction="vertical"
                    label={t("RECEIVER_NAME")}
                    value={passportHandover?.receiver_name ?? "-"}
                  />
                  <LabelValue
                    loading={isLoading}
                    direction="vertical"
                    label={t("NUSUK_RECEIPT_DATE")}
                    value={passportHandoverDate || "-"}
                  />
                  <LabelValue
                    loading={isLoading}
                    direction="vertical"
                    label={t("ENTITY_TYPE")}
                    value={formatEntityTypeValue(passportHandover?.entity_type)}
                  />
                  <LabelValue
                    loading={isLoading}
                    direction="vertical"
                    label={t("EXTERNAL_ENTITY_NAME")}
                    value={passportHandover?.external_entity_name ?? "-"}
                  />
                  <LabelValue
                    loading={isLoading}
                    direction="vertical"
                    label={t("INTERNAL_SECTOR")}
                    value={passportHandover?.internal_job_sector_id}
                    renderValue={(value) => (
                      <SelectCachedOptions.Display
                        value={value}
                        campareBy="id"
                        optionKey="job_sectors"
                        render={(sector) =>
                          getLocalizedText(sector?.name_ar, sector?.name_en) ||
                          "-"
                        }
                      />
                    )}
                  />
                  <LabelValue
                    loading={isLoading}
                    direction="vertical"
                    label={t("RECEIPT_REASON")}
                    value={passportHandover?.receipt_reason ?? "-"}
                  />
                  <LabelValue
                    loading={isLoading}
                    direction="vertical"
                    label={t("CREATED_BY")}
                    value={passportHandover?.created_by_name ?? "-"}
                  />
                  <LabelValue
                    loading={isLoading}
                    direction="vertical"
                    label={t("PRINTED_BY")}
                    value={passportHandover?.printed_by_name ?? "-"}
                  />
                  <LabelValue
                    loading={isLoading}
                    direction="vertical"
                    label={t("LAST_PRINTED_AT")}
                    value={
                      passportHandover?.last_printed_at
                        ? formatDateValue(passportHandover?.last_printed_at)
                        : "-"
                    }
                  />
                </STACK_4>
              </Card>
                )
              }
              
            </STACK_3>
          </COL>
        </Row>
        <PilgrimPrintSheet
          pilgrim={pilgrim}
          pilgrimId={id}
          printRequestKey={printRequestKey}
          onPrinted={() => setPrintRequestKey(null)}
        />
      </Container>
    </>
  );
};

const COL = ({ children }) => (
  <Col xs={12} md={6} xl={4}>
    {children}
  </Col>
);

const STACK_3 = ({ children }) => <Stack gap={3}>{children}</Stack>;
export const STACK_4 = ({ children }) => <Stack gap={4}>{children}</Stack>;
